import { emit } from '@zapier/events-toolkit-browser';

export const TransferClientErrorEventName =
  'zap.transfer.TransferClientErrorEvent';

export interface Meta {
  /** Is the system emitting the event */
  system_name: 'transfer';
  /** The git SHA (aka release) associated with the code that emitted this event */
  release_id: string;
}

export interface Context {
  /** The Zapier Account ID. This is the account that the user joined. Most users will have a fresh account created for them when they sign up but sometimes they'll join an existing account  */
  account_id: number | null;
}

export interface Event {
  /** A unique identifier for this event, used to associate it with `zap.transfer.TransferUserInteractionEvent` events via their `client_error_event_reference_id` field. */
  event_reference_id: string;
  /** An ID used to associate this error with Sentry logs. This ID may be generated by the backend where the error occurred and passed to the Workflows client in the request response. Otherwise, it is generated by the Workflows client. */
  error_id: string | null;
  /** The error's class name, such as `URIError` or `TransferClientError`. */
  error_name: string;
  /** A message that provides further detail about the error. */
  error_message: string | null;
  /** A human-readable title for the error. */
  error_title: string | null;
  /** A message that provides further detail about the error. */
  error_detail: string | null;
  /** A machine-readable code that provides more information about the error. */
  error_code: string | null;
  /** The HTTP status of the response that returned the error. */
  error_status: number | null;
  /** A serialized JSON blob with additional data related to the error. */
  error_meta: string | null;
  /** The URL of the request that returned the error. */
  request_url: string | null;
}

type ManagedContext = Omit<Meta, 'system_name'> & Partial<Context>;

export type TrackingFunction = (
  eventData: Event,
  eventContext?: Partial<Context>
) => Promise<void>;

export default function createTracking(initialContext: ManagedContext) {
  let ctx = initialContext;

  async function trackEvent(eventData: Event, eventContext?: Partial<Context>) {
    // merge in any new context so it's available _with_ the event
    ctx = { ...ctx, ...eventContext };

    // build the event
    const event: Meta & Context & Event = {
      // data
      event_reference_id: eventData.event_reference_id,
      error_id: eventData.error_id,
      error_name: eventData.error_name,
      error_message: eventData.error_message,
      error_title: eventData.error_title,
      error_detail: eventData.error_detail,
      error_code: eventData.error_code,
      error_status: eventData.error_status,
      error_meta: eventData.error_meta,
      request_url: eventData.request_url,

      // meta
      system_name: 'transfer',
      release_id: ctx.release_id,

      // context
      account_id: ctx.account_id || null,
    };

    await emit(event, TransferClientErrorEventName);
  }

  return trackEvent;
}
